import React from 'react';

import { Inline, Text } from '@atlaskit/primitives/compiled';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { AppIcon } from '../app-icon/app-icon';

import { IconStack } from './icon-stack';
import { getAppFromSource } from './utils';

const DEFAULT_MAX_SOURCES_IN_STACK = 3;

type SourcesStackProps = {
	maxSourcesInStack?: number;
	sources: NLPSearchResultSource[];
	showNumAddtlSources?: boolean;
	testId?: string;
	addEditorStyleOverrides?: boolean;
	/** When true, applies the notebook-style shuffled look to the icon stack. */
	shuffled?: boolean;
};

export function SourcesStack({
	maxSourcesInStack,
	sources,
	showNumAddtlSources,
	testId,
	addEditorStyleOverrides,
	shuffled = false,
}: SourcesStackProps): React.JSX.Element | null {
	if (sources.length === 0) {
		return null;
	}

	const sourcesForIconStack: NLPSearchResultSource[] = [];
	const sourceProductsMap: Record<string, boolean> = {};
	maxSourcesInStack = maxSourcesInStack ?? DEFAULT_MAX_SOURCES_IN_STACK;

	for (let i = 0; i < sources.length; i++) {
		const source = sources[i];
		const appKey = getAppFromSource(source.product, source.url) || 'Unknown';

		if (!sourceProductsMap[appKey]) {
			sourcesForIconStack.push(source);
			sourceProductsMap[appKey] = true;
		}

		if (sourcesForIconStack.length === maxSourcesInStack) {
			break;
		}
	}

	const appIcons = sourcesForIconStack.map((source) => (
		<AppIcon
			// still need to convert product to correct subproduct e.g. for Microsoft
			app={getAppFromSource(source.product, source.url)}
			data-testid={`app-icon-${source.ari}`}
			key={source.ari}
			size="xxsmall"
		/>
	));

	// want to display the number of additional sources that are not included in the appIcons stack
	const numAddtlSources = sources.length - appIcons.length;

	return appIcons.length > 0 ? (
		<Inline alignBlock="center" space="space.050">
			<IconStack
				testId={testId}
				addEditorStyleOverrides={addEditorStyleOverrides}
				shuffled={shuffled}
			>
				{appIcons}
			</IconStack>
			{showNumAddtlSources && numAddtlSources > 0 && (
				<Text color="color.text.subtle" size="small">
					+{numAddtlSources}
				</Text>
			)}
		</Inline>
	) : null;
}
